import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import FeeSchedule from "marketplace/teams/fee-schedules/fee-schedule";
import React, { useState } from "react";
import usePublishedFeeSchedules from "work/proposals/_views/drafts/tabs/use-published-fee-schedules";

// The same shape as the document panel's Upload Template: the label wraps rather than pushing the
// panel's toolbar wider than the pane on a narrow screen.
const ActionButton = styled(Button)(({ theme }) => ({
  whiteSpace: "normal",
  wordBreak: "keep-all",
  "& .MuiButton-startIcon": {
    flexShrink: 0,
  },
  [theme.breakpoints.down("md")]: {
    lineHeight: 1.1,
    maxWidth: theme.spacing(14),
  },
}));

type PublishedFeeScheduleActionProps = {
  /** The marketplace team the draft's vendor team came from — `VendorTeam.marketplaceId`. */
  marketplaceTeamId: Guid;
  /** Called with the schedule the vendor chose. The caller settles staffing and applies it. */
  onApply: (schedule: FeeSchedule) => void;
};

/**
 * Applies the vendor team's published marketplace fee schedule to its own draft, in one action
 * (#4920).
 *
 * The published schedule is the rate card the team already keeps and the one clients see, so the
 * vendor does not have to keep a template that copies it. A team with one published schedule gets
 * a button that applies it. A team with several gets the same button, which asks which one.
 *
 * It draws nothing while the schedules first load, or when the team publishes no rates: there is then
 * nothing to apply. It offers the schedules only while the latest load succeeded (`isSuccess`, which
 * RTK Query keeps through an ordinary background refetch). The store keeps what the last successful
 * load returned, and after a failed load those may no longer be the team's prices, so a failed load
 * offers a retry instead, and so does the retry while it runs: `isError` is false again then.
 */
export default function PublishedFeeScheduleAction(props: Readonly<PublishedFeeScheduleActionProps>) {
  const { marketplaceTeamId, onApply } = props;

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // A schedule with no rates in it would apply nothing, so it is not offered.
  const { schedules, schedulesQuery } = usePublishedFeeSchedules(marketplaceTeamId);

  const loadFailed =
    schedulesQuery.isError || (schedulesQuery.isFetching && !schedulesQuery.isSuccess && !schedulesQuery.isLoading);
  if (loadFailed) {
    return (
      <Tooltip describeChild title="Your team's published rates could not be loaded. Retry to apply them.">
        <span>
          <ActionButton
            color="primary"
            disabled={schedulesQuery.isFetching}
            startIcon={<RefreshIcon />}
            onClick={() => void schedulesQuery.refetch()}
          >
            Retry Published Rates
          </ActionButton>
        </span>
      </Tooltip>
    );
  }
  if (!schedulesQuery.isSuccess || schedules.length === 0) return null;

  function handleScheduleChosen(schedule: FeeSchedule) {
    setMenuAnchor(null);
    onApply(schedule);
  }

  function handleButtonClicked(event: React.MouseEvent<HTMLElement>) {
    if (schedules.length === 1) {
      onApply(schedules[0]);
      return;
    }
    setMenuAnchor(event.currentTarget);
  }

  const tooltip =
    schedules.length === 1
      ? `Add the rates your team publishes as "${schedules[0].name}" (${schedules[0].categoryCountLabel})`
      : "Add the rates from one of your team's published fee schedules";

  return (
    <>
      {/* Describes the button rather than renaming it: its name stays the words it shows. */}
      <Tooltip describeChild title={tooltip}>
        <ActionButton
          color="primary"
          startIcon={<StorefrontIcon />}
          aria-haspopup={schedules.length > 1 ? "menu" : undefined}
          aria-expanded={menuAnchor ? "true" : undefined}
          onClick={handleButtonClicked}
        >
          Apply Published Rates
        </ActionButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {schedules.map((schedule) => (
          <MenuItem key={schedule.id.value} onClick={() => handleScheduleChosen(schedule)}>
            <ListItemText primary={schedule.name} secondary={schedule.categoryCountLabel} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
